import { CustomError } from '@/utils/CustomError';
import { forceLogoutToLogin, storage } from './storageService';

const API = import.meta.env.VITE_API_URL;

function hasJsonRequestBody(options: RequestInit): boolean {
  const { body } = options;
  if (body === undefined || body === null) return false;
  if (typeof body === 'string') return body.length > 0;
  // FormData, Blob, ArrayBuffer, etc.
  return true;
}

/** Erros de payload/resposta acima do limite (proxy, gateway, servidor). */
function isOversizedHttpError(
  status: number,
  message: string,
  errorCode?: string,
): boolean {
  if (status === 413) return true;

  const text = `${message} ${errorCode ?? ''}`.toLowerCase();
  return (
    text.includes('max return response') ||
    text.includes('maximum response') ||
    text.includes('response too large') ||
    text.includes('response size') ||
    text.includes('request entity too large') ||
    text.includes('payload too large') ||
    text.includes('content too large') ||
    text.includes('body too large') ||
    /max.*response|response.*max|entity.*too.?large/.test(text)
  );
}

// Função para fazer requisições com interceptor
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = storage.get<string>('token');

  const headers = new Headers(options.headers);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // DELETE/GET sem corpo com `Content-Type: application/json` costuma quebrar em Safari:
  // o servidor pode tentar parsear JSON vazio e falhar. Só envia o header quando há body JSON.
  if (hasJsonRequestBody(options) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const config: RequestInit = {
    ...options,
    credentials: 'include',
    mode: 'cors',
    headers,
  };

  const response = await fetch(`${API}${endpoint}`, config);
  const raw = await response.text();

  let data: Record<string, unknown> = {};
  if (raw) {
    try {
      data = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      data = { message: raw };
    }
  }

  const nested = data.data as Record<string, unknown> | undefined;
  const message = typeof data.message === 'string' ? data.message : raw;
  const errorCode = typeof data.errorCode === 'string' ? data.errorCode : undefined;

  // Verifica se é erro de autenticação
  if (response.status === 401 ||
      nested?.error === 'UNAUTHENTICATED' ||
      data.error === 'UNAUTHENTICATED') {

    // Limpa sessão (mantém lastLoginProfile: nome, email, avatar para a tela de login)
    storage.remove('token');
    storage.remove('user');
    storage.remove('activeOrganization');

    // Redireciona para login
    window.location.href = '/auth';

    throw new Error('UNAUTHENTICATED');
  }

  if (!response.ok && isOversizedHttpError(response.status, message, errorCode)) {
    forceLogoutToLogin('HTTP_RESPONSE_TOO_LARGE');
  }

  if (!response.ok) {
    throw new CustomError(
      typeof data.message === 'string' ? data.message : 'Erro na requisição',
      typeof data.errorCode === 'string' ? data.errorCode : 'UNKNOWN',
      response.status,
      data.error,
      data.validation as CustomError['validation'],
    );
  }

  return data as T;
}
